/** @jsxImportSource @emotion/react */

import { FC, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import IconButton from '@mui/material/IconButton'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import Input from 'components/Input'
import { findOneQuestionPassageBank_findOneQuestionPassageBank_questionPassages as QuestionPassage } from '__generatedTypes__/findOneQuestionPassageBank'
import { updateCARSQuestionTitle, updateCARSQuestionTitleVariables } from '__generatedTypes__/updateCARSQuestionTitle'
import { findOneQuestionPassage, findOneQuestionPassageVariables } from '__generatedTypes__/findOneQuestionPassage'
import { GET_ONE_QUESTION_PASSAGE, UPDATE_QUESTION_PASSAGE_TITLE } from 'queries/questionPassage'
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg'
import { toast } from 'utils/toast'
import * as classes from '../../styles'

export type FormValues = {
  title: string
}

type Props = {
  questionPassage: QuestionPassage
  onSuccessfulEdit: (questionPassage: QuestionPassage) => void
  onStopEditingTitle: (e: React.SyntheticEvent<HTMLButtonElement>) => void
}

export const EditTitleForm: FC<Props> = ({ questionPassage, onSuccessfulEdit, onStopEditingTitle }) => {
  const [title, setTitle] = useState(questionPassage.title)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [updateQuestionPassageTitle] = useMutation<updateCARSQuestionTitle, updateCARSQuestionTitleVariables>(
    UPDATE_QUESTION_PASSAGE_TITLE
  )
  const { refetch } = useQuery<findOneQuestionPassage, findOneQuestionPassageVariables>(GET_ONE_QUESTION_PASSAGE, {
    variables: {
      params: questionPassage.id,
    },
    fetchPolicy: 'network-only',
  })

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    try {
      setIsSubmitting(true)
      if (!title) {
        return
      }

      await updateQuestionPassageTitle({
        variables: {
          id: questionPassage.id,
          title: title,
        },
      })

      await refetch().then(({ data }) => {
        if (data.findOneQuestionPassage) {
          onSuccessfulEdit?.(data.findOneQuestionPassage)
          return
        }
        throw new Error('Failed to refetch CARS Question data')
      })

      toast.success('CARS Question title updated successfully')
    } catch (error) {
      console.error(error)
      toast.error('Failed to update CARS Question title')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCheckButtonClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    handleSubmit(e)
  }

  return (
    <div onSubmit={handleSubmit} css={classes.editTitleForm}>
      <Input
        css={classes.titleInput}
        size="small"
        value={title}
        name="title"
        placeholder="Title"
        error={!title}
        fullWidth
        onClick={(e) => e.stopPropagation()}
        onChange={(value) => setTitle(value)}
        disabled={isSubmitting}
      />

      <div css={classes.titleFormActions}>
        <IconButton onClick={handleCheckButtonClick} css={classes.iconButton} disabled={isSubmitting}>
          <CheckIcon />
        </IconButton>
        <IconButton onClick={onStopEditingTitle} css={classes.iconButton}>
          <CloseRoundedIcon css={classes.iconCross} />
        </IconButton>
      </div>
    </div>
  )
}
